import { useCallback, useMemo } from "react";
import { useLocalSearchParams, usePathname } from "expo-router";
import type { NavSidePanelItem } from "@visioni/a3s-ui";
import { getCameraKeyString, getCameraSourceAndFleet, formatCameraLabel } from "@src/utils/key-parser";
import { useCamerasListState, type CameraApiResponse } from "@src/features/cameras";
import { useCameraCollection } from "./useCameraCollection";
import { useSiteNames } from "./useSiteNames";
import { useSnapshots, type SignedSnapshot } from "@visioni/a3s-media/app";
import { useAmplitude } from "@src/hooks/useAmplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";

export interface CameraNavItemData {
  readonly camera: CameraApiResponse;
  readonly snapshot: SignedSnapshot | null;
  readonly siteId: string;
  readonly href: string;
}

export interface SiteNavItemData {
  readonly siteId: string;
  readonly siteName: string;
  readonly siteSubtitle?: string;
  readonly thumbnailUrl: string | null;
}

export type NavItemMetadata = CameraNavItemData | SiteNavItemData;

interface UseCameraSideNavItemsParams {
  readonly activeCameraKey?: string;
}

interface UseCameraSideNavItemsResult {
  readonly items: readonly NavSidePanelItem[];
  readonly itemDataMap: ReadonlyMap<string, NavItemMetadata>;
  readonly status: "pending" | "error" | "success";
  readonly isEmpty: boolean;
}

export function useCameraSideNavItems({ activeCameraKey }: UseCameraSideNavItemsParams): UseCameraSideNavItemsResult {
  const pathname = usePathname();
  const params = useLocalSearchParams<{ siteId?: string }>();
  const { track } = useAmplitude();
  const { data: snapshots } = useSnapshots({});
  const siteNames = useSiteNames();

  const siteId = params.siteId;
  const listState = useCamerasListState(siteId);
  const allCameras = listState.status === "success" ? listState.cameras : undefined;

  const { groupedCameras } = useCameraCollection({
    cameras: allCameras,
    searchQuery: "",
    sortBy: null,
    siteNames,
  });

  const isAlarmSettingsScreen = pathname.includes("/alarm");

  const trackCameraClick = useCallback(
    (camera: CameraApiResponse) => {
      const cameraKey = getCameraKeyString(camera);
      const sourceAndFleet = getCameraSourceAndFleet(camera);
      track(ANALYTICS_EVENTS.CAMERA_CLICKED, {
        camera_id: cameraKey,
        ...(sourceAndFleet && {
          source: sourceAndFleet.source,
          generation: sourceAndFleet.fleet,
        }),
        tenant_id: camera.site.tenant,
        site_id: camera.site.id,
      });
    },
    [track],
  );

  const { items, itemDataMap } = useMemo(() => {
    const dataMap = new Map<string, NavItemMetadata>();

    if (listState.status === "pending") {
      return {
        items: [{ id: "cameras-loading", label: "Loading cameras...", disabled: true }] as NavSidePanelItem[],
        itemDataMap: dataMap,
      };
    }

    if (listState.status === "error") {
      return {
        items: [{ id: "cameras-error", label: "Failed to load cameras", disabled: true }] as NavSidePanelItem[],
        itemDataMap: dataMap,
      };
    }

    if (groupedCameras.length === 0) {
      return {
        items: [{ id: "cameras-empty", label: "No cameras available", disabled: true }] as NavSidePanelItem[],
        itemDataMap: dataMap,
      };
    }

    const buildCameraItem = (camera: CameraApiResponse): NavSidePanelItem => {
      const cameraKey = getCameraKeyString(camera);
      const snapshot = snapshots?.[`${camera.key.asset}@${camera.key.id}`] ?? null;
      const itemId = `camera-${cameraKey}`;
      const displayName = camera.name || formatCameraLabel(cameraKey);
      const subtitleName = camera.name ? formatCameraLabel(cameraKey) : undefined;

      const basePath = `/tenants/${camera.site.tenant}/sites/${camera.site.id}/cameras/${encodeURIComponent(cameraKey)}`;
      const href = isAlarmSettingsScreen ? `${basePath}/alarm` : basePath;

      dataMap.set(itemId, { camera, snapshot, siteId: camera.site.id, href });

      return {
        id: itemId,
        label: displayName,
        secondaryLabel: subtitleName,
        selected: cameraKey === activeCameraKey,
        onPress: () => trackCameraClick(camera),
      };
    };

    const hasSingleSite = groupedCameras.length === 1;

    if (hasSingleSite) {
      const navItems = groupedCameras[0].cameras.map(buildCameraItem);
      return { items: navItems, itemDataMap: dataMap };
    }

    const navItems: NavSidePanelItem[] = groupedCameras.map((group) => {
      const containsSelectedCamera = group.cameras.some((c) => getCameraKeyString(c) === activeCameraKey);
      const thumbnailUrl = group.cameras[0]?.snapshot?.url ?? null;
      const siteItemId = `site-${group.siteId}`;

      dataMap.set(siteItemId, {
        siteId: group.siteId,
        siteName: group.siteName,
        siteSubtitle: group.siteSubtitle,
        thumbnailUrl,
      });

      return {
        id: siteItemId,
        label: group.siteName,
        secondaryLabel: group.siteSubtitle,
        defaultExpanded: containsSelectedCamera,
        selected: containsSelectedCamera,
        children: group.cameras.map(buildCameraItem),
      };
    });

    return { items: navItems, itemDataMap: dataMap };
  }, [listState.status, groupedCameras, snapshots, activeCameraKey, trackCameraClick, isAlarmSettingsScreen]);

  return {
    items,
    itemDataMap,
    status:
      listState.status === "pending" ? "pending"
      : listState.status === "error" ? "error"
      : "success",
    isEmpty: listState.status === "success" && groupedCameras.length === 0,
  };
}
